import { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import { useSelector } from "react-redux";

import { dispatch, getState } from "../../../../state/store";
import {
  setIsOriginalEmailSetted,
  setOriginalEmailMessage,
  setSelectedApp
} from "../../../../state/ducks";

import { t } from "../../../../services";
import { appManageTypes, strCode } from "../../../../consts";

import { FlexContainers, TextEditor, WrapperContainer } from "../../../../components";
import { EmailTemplateHint, EmailTemplateText } from "./styled";
import { selectSelectedApp } from "../../../../state/selectors";

const getClearEditorValue = (value) => {
  return value
    .replaceAll("<p>", "")
    .replaceAll("</p>", "")
    .replaceAll(/&nbsp;/g, "")
    .replaceAll(/\n/g, "");
};

const EmailTab = () => {
  const selectedApp = useSelector((store) => store.apps.selectedApp);
  const manageType = useSelector((store) => store.apps.manageType);
  const isEmailMessageEditing = useSelector((store) => store.apps.isEmailMessageEditing);
  const originalEmailMessage = useSelector((store) => store.apps.originalEmailMessage);

  const [emailMessage, setEmailMessage] = useState(selectedApp?.emailMessage ?? "");
  const isFirstRender = useRef(true);

  const debouncedSaveEmailMessage = useCallback(
    debounce((value) => {
      const currSelectedApp = selectSelectedApp()(getState());

      if (getClearEditorValue(value)) {
        dispatch(setSelectedApp({ ...currSelectedApp, emailMessage: value }));
      } else {
        dispatch(setSelectedApp({ ...currSelectedApp, emailMessage: "" }));
      }
    }, 500),
    [dispatch, manageType, selectedApp]
  );

  useEffect(() => {
    if (
      selectedApp?.emailMessage &&
      !originalEmailMessage &&
      manageType === appManageTypes.EDIT &&
      !isEmailMessageEditing
    ) {
      dispatch(setOriginalEmailMessage(selectedApp.emailMessage));
      dispatch(setIsOriginalEmailSetted(true));
    }
  }, []);

  useEffect(() => {
    if (
      selectedApp?.emailMessage &&
      originalEmailMessage &&
      selectedApp.emailMessage !== originalEmailMessage &&
      !isEmailMessageEditing
    ) {
      setEmailMessage(originalEmailMessage);
    }
  }, []);

  useEffect(() => {
    if (
      !isEmailMessageEditing &&
      !selectedApp?.emailMessage &&
      originalEmailMessage !== emailMessage &&
      manageType === appManageTypes.EDIT
    ) {
      setEmailMessage("");
    }
  }, [isEmailMessageEditing, originalEmailMessage, emailMessage, manageType]);

  useEffect(() => {
    if (!isEmailMessageEditing && manageType === appManageTypes.EDIT) {
      setEmailMessage(originalEmailMessage);
    }
  }, [originalEmailMessage, isEmailMessageEditing, emailMessage, manageType]);

  useEffect(() => {
    if (
      !isEmailMessageEditing &&
      selectedApp?.emailMessage !== originalEmailMessage &&
      manageType === appManageTypes.EDIT
    ) {
      dispatch(setSelectedApp({ ...selectedApp, emailMessage: originalEmailMessage }));
      setEmailMessage(originalEmailMessage);
    }
  }, [isEmailMessageEditing, selectedApp, emailMessage, manageType]);

  const handleEmailMessageChange = (value) => {
    const clearedValue = getClearEditorValue(value);

    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }

    if (
      selectedApp &&
      ((clearedValue && clearedValue !== getClearEditorValue(selectedApp?.emailMessage)) ||
        (!clearedValue &&
          (isEmailMessageEditing || manageType === appManageTypes.FROM_EXISTING || !manageType))) &&
      (isEmailMessageEditing || manageType !== appManageTypes.EDIT)
    ) {
      debouncedSaveEmailMessage(value);
    }
  };

  return (
    <WrapperContainer
      Container={FlexContainers.FlexColumnStart}
      width="100%"
      alignItems="start"
      pt={20}
      pb={17}>
      <EmailTemplateHint>{t(strCode.EMAIL_TEMPLATE_HINT)}</EmailTemplateHint>

      <EmailTemplateText
        dangerouslySetInnerHTML={{
          __html: t(strCode.EMAIL_TEMPLATE_GREETINGS)
        }}
      />

      <WrapperContainer mv={16} pr={24} width="100%" height={290}>
        <TextEditor
          onChange={handleEmailMessageChange}
          viewOnly={manageType === appManageTypes.EDIT && !isEmailMessageEditing}
          placeholder={t(strCode.ENTER_ADDITIONAL_TEXT_PLACEHOLDER)}
          initialHtmlMarkup={emailMessage}
          small
          disabledHeaderVisible
        />
      </WrapperContainer>

      <EmailTemplateText
        dangerouslySetInnerHTML={{
          __html: t(strCode.EMAIL_TEMPLATE_DETAILS)
        }}
      />
    </WrapperContainer>
  );
};

export default EmailTab;
